import React, { useEffect } from 'react';
import { Form, Skeleton } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPen, faPhone, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  InfoRow,
  InfoLabel,
  InfoValue,
  FormSection,
  StyledInput,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  ActionIconButton,
} from '../Styles';
import { CARD_ICON_STYLE, INFO_ICON_STYLE, validatePhone } from '../constants';
import { useTheme } from '../../../context/ThemeContext';
import type { AddressSectionProps, AddressFormValues } from '../types';

const AddressSection: React.FC<AddressSectionProps> = ({
  user,
  editingSection,
  loading,
  profileLoading,
  onEdit,
  onSave,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();

  const { handleSubmit, control, reset } = useForm<AddressFormValues>({
    defaultValues: { address: '', publicPhone: '' },
  });

  useEffect(() => {
    if (user) reset({ address: user.address || '', publicPhone: user.publicPhone || '' });
  }, [user, reset]);

  const handleCancel = () => { reset(); onCancel(); };

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faBuilding} style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Business Information</CardTitleText>
        </CardTitle>
        <ActionIconButton onClick={profileLoading ? undefined : onEdit} iconColor="#2D6CCA" $loading={profileLoading}>
          <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
        </ActionIconButton>
      </CardHeader>

      {editingSection !== 'address' ? (
        <div>
          <InfoRow>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={INFO_ICON_STYLE} />
            <InfoLabel>Address:</InfoLabel>
            <InfoValue>
              {profileLoading
                ? <Skeleton.Input active size="small" style={{ width: 100, height: 20 }} />
                : user?.address || 'Location not provided'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <FontAwesomeIcon icon={faPhone} style={INFO_ICON_STYLE} />
            <InfoLabel>Public Phone:</InfoLabel>
            <InfoValue>
              {profileLoading
                ? <Skeleton.Input active size="small" style={{ width: 150, height: 20 }} />
                : user?.publicPhone || 'Not provided'}
            </InfoValue>
          </InfoRow>
        </div>
      ) : (
        <Form layout="vertical" onFinish={handleSubmit(onSave)}>
          <FormSection>
            <Controller
              name="address"
              control={control}
              rules={{ required: 'Business address is required' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Business Address"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <StyledInput {...field} placeholder="Enter your business address" />
                </Form.Item>
              )}
            />
            <Controller
              name="publicPhone"
              control={control}
              rules={{ validate: (v) => !v || validatePhone(v) || 'Please enter a valid phone number' }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Public Phone Number"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <StyledInput {...field} placeholder="Enter public phone number for customers" />
                </Form.Item>
              )}
            />
            <ActionButtons>
              <SaveChangesButton icon={<CheckOutlined />} htmlType="submit" loading={loading}>
                Save Changes
              </SaveChangesButton>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            </ActionButtons>
          </FormSection>
        </Form>
      )}
    </SettingsCard>
  );
};

export default React.memo(AddressSection);
